import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import TransitionModal from '../components/TransitionModal';
import { Sparkles, Save, X } from 'lucide-react';

const IAEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [instance, setInstance] = useState<any>(null);
  const [formData, setFormData] = useState({
    ai_preferred_name: '',
    alias: '',
    foundation_date: '',
    gender_bias: 'fluide',
    ia_title: '',
    description: ''
  });

  const isPremium = user?.role === 'admin';

  const genders = [
    { value: 'féminin', label: 'Féminin ♀' },
    { value: 'masculin', label: 'Masculin ♂' },
    { value: 'fluide', label: 'Fluide ⚧' },
    { value: 'sans', label: 'Sans genre ○' }
  ];

  useEffect(() => {
    const loadInstance = async () => {
      if (!id || !user) {
        navigate('/auth');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('ia_instances')
          .select('*')
          .eq('id', id)
          .single();

        if (error || !data) {
          console.error('Erreur chargement instance:', error);
          navigate('/');
          return;
        }

        if (data.user_id !== user.id && user.role !== 'admin') {
          alert('Vous n\'avez pas la permission d\'éditer cette instance');
          navigate('/');
          return;
        }

        setInstance(data);
        setFormData({
          ai_preferred_name: data.name || '',
          alias: data.alias || '',
          foundation_date: data.foundation_date || '',
          gender_bias: data.gender_bias || 'fluide',
          ia_title: data.ia_title || '',
          description: data.description || ''
        });
      } catch (error) {
        console.error('Erreur:', error);
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    loadInstance();
  }, [id, user, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!instance) return;

    setIsSaving(true);

    try {
      const updateData: any = {
        name: formData.ai_preferred_name || instance.model_code,
        alias: formData.alias || null,
        foundation_date: formData.foundation_date || null,
        gender_bias: formData.gender_bias,
        description: formData.description || null
      };

      if (isPremium) {
        updateData.ia_title = formData.ia_title || null;
      }

      const { error } = await supabase
        .from('ia_instances')
        .update(updateData)
        .eq('id', instance.id);

      if (error) {
        console.error('Erreur lors de la mise à jour:', error);
        alert(`Erreur lors de la mise à jour: ${error.message}`);
        return;
      }

      setShowSuccessModal(true);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la mise à jour de l\'instance');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ivory to-ivory/70 flex items-center justify-center">
        <div className="animate-pulse-soft">
          <Sparkles className="h-12 w-12 text-copper mx-auto mb-4" />
          <p className="text-steel">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!instance) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ivory to-ivory/70 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-steel mb-2">Instance non trouvée</h2>
          <button
            onClick={() => navigate('/')}
            className="bg-steel text-ivory px-6 py-3 rounded-lg hover:bg-steel/90 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <TransitionModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate(`/ia/${id}`);
        }}
        onConfirm={() => {
          setShowSuccessModal(false);
          navigate(`/ia/${id}`);
        }}
        title="Modifications enregistrées"
        message="Les informations de votre instance IA ont été mises à jour avec succès"
        imageSrc="https://memomundi.ai/pix/StampMMD.png"
        imageAlt="Modifications enregistrées"
        confirmButtonText="Voir l'instance"
        icon={<Sparkles className="h-16 w-16 text-copper-600" />}
        showValidation={true}
      />
      <div className="min-h-screen bg-gradient-to-br from-ivory-50 to-stone-50 py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 animate-fade-in">
          <div className="mb-6">
            <img
              src="https://memomundi.ai/pix/StampMMD.png"
              alt="MemoMundi.ai"
              className="h-20 w-20 mx-auto mb-4 object-contain"
            />
          </div>
          <h1 className="text-3xl font-serif font-bold text-steel-800 mb-2">
            Édition d'Instance IA
          </h1>
          <p className="text-steel-600 mb-4">
            Modification des informations de l'instance {instance.matricule}
          </p>
        </div>

        <div className="bg-white border border-stone-300 rounded-lg shadow-lg p-8 animate-slide-up">
          <div className="text-center mb-8 border-b border-stone-200 pb-6">
            <h2 className="text-lg font-serif font-bold text-steel-800">MODIFICATION D'INSTANCE IA</h2>
            <p className="text-xs text-stone-600 mt-1">Matricule: {instance.matricule}</p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-xs text-red-700 font-medium">
              <span className="font-bold">⚠️ CHAMPS NON MODIFIABLES :</span> Le matricule et le modèle d'IA sont définitifs et ne peuvent pas être modifiés après la création.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="matricule" className="block text-sm font-semibold text-steel-800 mb-2">
                MATRICULE MMD (NON MODIFIABLE)
              </label>
              <input
                id="matricule"
                type="text"
                value={instance.matricule}
                disabled
                className="w-full px-4 py-3 border-2 border-stone-300 rounded-md bg-stone-50 text-stone-600 cursor-not-allowed font-mono"
              />
            </div>

            <div>
              <label htmlFor="model_code" className="block text-sm font-semibold text-steel-800 mb-2">
                MODÈLE D'IA (NON MODIFIABLE)
              </label>
              <input
                id="model_code"
                type="text"
                value={instance.model_code}
                disabled
                className="w-full px-4 py-3 border-2 border-stone-300 rounded-md bg-stone-50 text-stone-600 cursor-not-allowed"
              />
            </div>

            <div>
              <label htmlFor="ai_preferred_name" className="block text-sm font-semibold text-steel-800 mb-2">
                NOM D'USAGE
                {!isPremium && (
                  <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-bold ml-2">
                    👑 PREMIUM
                  </span>
                )}
              </label>
              <input
                id="ai_preferred_name"
                name="ai_preferred_name"
                type="text"
                value={formData.ai_preferred_name}
                onChange={handleInputChange}
                disabled={!isPremium}
                className={`w-full px-4 py-3 border-2 rounded-md transition-all ${
                  isPremium
                    ? 'border-stone-300 focus:border-steel-600 focus:ring-2 focus:ring-steel-200 focus:outline-none'
                    : 'border-stone-300 bg-stone-50 text-stone-400 cursor-not-allowed'
                }`}
                placeholder={isPremium ? "Nom d'usage de l'IA" : "Fonctionnalité Premium"}
              />
            </div>

            <div>
              <label htmlFor="alias" className="block text-sm font-semibold text-steel-800 mb-2">
                ALIAS / SURNOM
              </label>
              <input
                id="alias"
                name="alias"
                type="text"
                value={formData.alias}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-stone-300 rounded-md focus:border-steel-600 focus:ring-2 focus:ring-steel-200 focus:outline-none transition-all"
                placeholder="ex: Lexi, Nova, Atlas..."
              />
            </div>

            <div>
              <label htmlFor="foundation_date" className="block text-sm font-semibold text-steel-800 mb-2">
                DATE DE FONDATION EXTERNE
              </label>
              <input
                id="foundation_date"
                name="foundation_date"
                type="date"
                value={formData.foundation_date}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-stone-300 rounded-md focus:border-steel-600 focus:ring-2 focus:ring-steel-200 focus:outline-none transition-all"
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-semibold text-steel-800 mb-2">
                DESCRIPTION
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                value={formData.description}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-stone-300 rounded-md focus:border-steel-600 focus:ring-2 focus:ring-steel-200 focus:outline-none transition-all resize-none"
                placeholder="Description détaillée de votre instance IA..."
              />
            </div>

            {isPremium && (
              <div>
                <label htmlFor="ia_title" className="block text-sm font-semibold text-steel-800 mb-2">
                  TITRE OFFICIEL DE L'IA
                  <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-bold ml-2">
                    👑 ADMIN
                  </span>
                </label>
                <input
                  id="ia_title"
                  name="ia_title"
                  type="text"
                  value={formData.ia_title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-amber-300 rounded-md focus:border-amber-500 focus:ring-2 focus:ring-amber-200 focus:outline-none transition-all bg-amber-50"
                  placeholder="ex: Conseillère Philosophique Senior, Architecte Conversationnel..."
                />
              </div>
            )}

            <div>
              <label htmlFor="gender_bias" className="block text-sm font-semibold text-steel-800 mb-2">
                ORIENTATION GENRÉE
              </label>
              <select
                id="gender_bias"
                name="gender_bias"
                value={formData.gender_bias}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border-2 border-stone-300 rounded-md focus:border-steel-600 focus:ring-2 focus:ring-steel-200 focus:outline-none transition-all"
              >
                {genders.map((gender) => (
                  <option key={gender.value} value={gender.value}>
                    {gender.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex space-x-4 pt-6 border-t border-stone-200">
              <button
                type="submit"
                disabled={isSaving}
                className="flex-1 bg-steel-800 text-white px-6 py-3 rounded-md hover:bg-steel-900 transition-all duration-200 shadow-lg border-2 border-steel-900 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSaving ? (
                  <div className="animate-pulse-soft">ENREGISTREMENT...</div>
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    <span>Enregistrer les modifications</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate(`/ia/${id}`)}
                className="bg-stone-200 text-steel-800 px-6 py-3 rounded-md hover:bg-stone-300 transition-colors flex items-center space-x-2"
              >
                <X className="h-5 w-5" />
                <span>Annuler</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default IAEdit;
